import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Grid,
  IconButton,
  Modal,
  Stack,
  Typography,
  Box
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { IconPlus } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MainCard from 'ui-component/cards/MainCard';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import SearchBar from 'ui-component/SearchBar/SearchBar';
import { del, get } from '../../../axios/apiService';
import toast from 'react-hot-toast';
import ConfirmActionModal from 'ui-component/Modals/ConfirmActionModal';
import ActionButtons from 'ui-component/actionButton/ActionButtons';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';
import { Visibility } from '@mui/icons-material';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4
};

const RoleManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.Role_Management);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const skip = paginationModel.page * paginationModel.pageSize;
      const top = paginationModel.pageSize;

      const sortField = sortModel[0]?.field || '';
      const sortOrder = sortModel[0]?.sort === 'asc' ? 'Ascending' : 'Descending';
      const sortQuery = sortField ? `&$orderby=${sortField}&$orderbydirection=${sortOrder}` : '';

      const searchQuery = search ? `&$filter=Name~LIKE~${search} OR Description~LIKE~${search}` : '';

      const response = await get(
        `AdminRoles?$skip=${skip}&$top=${top}${searchQuery}${sortQuery}`
      );

      if (!response?.values) {
        setRoles([]);
        setRowCount(0);
        return;
      }

      setRoles(response.values);
      setRowCount(response.totalCount);
    } catch (err) {
      console.error('Failed to load roles', err);
      toast.error('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  }, [paginationModel, sortModel, search]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleDeleteClick = (roleId, roleName) => {
    setSelectedRoleId(roleId);
    setSelectedRoleName(roleName);
    setDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteModalOpen(false);
    setSelectedRoleId('');
    setSelectedRoleName('');
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRoleId) {
      handleCloseModal();
      return;
    }

    setIsLoading(true);
    try {
      await del(`AdminRoles/${selectedRoleId}`);
      toast.success('Role deleted successfully');
      handleCloseModal();
      setPaginationModel({ page: 0, pageSize: 10 });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error?.response?.data || 'Failed to delete role');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 1
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2
    },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params) => <ActionButtons
        actions={[
          {
            tooltip: "Edit Role",
            icon: canEdit ? <EditIcon /> : <Visibility />,
            color: "primary",
            size: "large",
            onClick: () => navigate(`/role-management/edit-role/${params.id}`)
          },
          ...(
            canEdit ? [
              {
                tooltip: "Delete Role",
                icon: <DeleteIcon />,
                color: "error",
                size: "large",
                onClick: () => handleDeleteClick(params.id, params.name)
              }
            ] : [])
        ]}
      />,
      align: 'right',
      width: 120,
      sortable: false
    }
  ];

  return (
    <Box>
      <MainCard title="Admin Role Management">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="end" alignItems="center">
              <SearchBar
                value={search}
                onChange={handleSearchChange}
                placeholder="Search"
              />
              {
                canEdit && <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/role-management/add-role')}
                  startIcon={<IconPlus />}
                >
                  Add
                </Button>
              }
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <DataGrid
              rows={roles}
              columns={columns}
              getRowId={(row) => row.id}
              sortingMode="server"
              paginationMode="server"
              rowCount={rowCount}
              pageSizeOptions={[10]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              autoHeight
              disableSelectionOnClick
            />
          </Grid>
        </Grid>
      </MainCard>

      <ConfirmActionModal
        open={deleteModalOpen}
        onClose={handleCloseModal}
        title="Delete Role"
        message="Are you sure you want to delete this role?"
        confirmText="Yes, Delete"
        onConfirm={handleDeleteConfirm}
      />
      <LoadingBackdrop open={isLoading} />
    </Box>
  );
};

export default RoleManagement;